"use client";

import React from "react";
import { Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stats = [{
  value: "$1B+",
  label: "PE FUND"
}, {
  value: "$400M+",
  label: "VC AUM — GP"
}, {
  value: "$100M",
  label: "Alt Assets"
}, {
  value: "52+",
  label: "Portfolio Cos."
}, {
  value: "3",
  label: "Continents"
}, {
  value: "25+",
  label: "Years PE/VC/Ops"
}];
const valueAdds = [{
  title: "Institutional Governance Built",
  desc: "Prior experience building and operating institutional fund governance infrastructure, including LP reporting cadences, portfolio monitoring, risk frameworks, and fund-level compliance, means N2N launches with institutional-grade back-office discipline already in place, not as a build-out item."
}, {
  title: "Multi-Asset Capital Deployment",
  desc: "25+ years leading primary and secondary investment activity across real estate, growth equity, pre-IPO, buyouts, and venture capital, the breadth of capital allocation experience that enables N2N to structure hybrid LP vehicles spanning infrastructure equity and real asset co-invests."
}, {
  title: "Fund Administration & LP DD Readiness",
  desc: "Hands-on experience with DDQ processes, financial statement analysis, audit coordination, and LP reporting — bringing institutional-grade discipline from day one."
}, {
  title: "Cross-Border JV & SPV Structuring",
  desc: "Executed JV and SPV agreements across the U.S., MENA, and India — applicable to N2N’s India land structures, solar rights agreements, and GP entity governance."
}, {
  title: "India & APAC Operational Presence",
  desc: "Active deal execution across Asia-Pacific and MENA supports regulatory navigation, government MoU processes, and local partner sourcing."
}, {
  title: "Venture Portfolio Intelligence",
  desc: "As GP of Aurum Venture Partners, brings live insight into AI infrastructure demand curves across leading frontier AI companies and compute-heavy platforms."
}];
export default function NarsiProfileSection() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section ref={ref} className="relative overflow-hidden bg-[#FDFAF3] py-24 text-[#1C1917] md:py-32">
      <div className="pointer-events-none absolute left-0 top-0 h-[560px] w-[560px] -translate-x-1/3 -translate-y-1/3 rounded-full bg-[#E7D8B8]/55 blur-[100px]" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-6 md:px-10 lg:grid-cols-[340px_1fr] lg:gap-16">
        <aside className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="group relative overflow-hidden rounded-xl border border-[#D8C29A] bg-[#F5EDD8] shadow-sm">
            <div className="aspect-[4/5] overflow-hidden">
              <img src="https://decent-kid.10web.cloud/wp-content/uploads/2026/03/Narsi-2-scaled.jpg?v=6f5duw2tdve" alt="Narsi Ayyagari" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>

            <div className="absolute inset-x-0 bottom-0 flex items-end justify-center bg-gradient-to-t from-black/90 via-black/55 to-transparent p-6 pt-24 opacity-0 translate-y-full transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
              <Button asChild className="w-full rounded-sm bg-[#B45309] text-white hover:bg-[#B45309]/90 font-mono tracking-wider">
                <Link to="https://www.linkedin.com/in/narsi-ayyagari-5608271/" newTab>
                  <Linkedin className="mr-2 h-4 w-4" />
                  Direct Contact
                </Link>
              </Button>
            </div>

            <div className="h-1 bg-[#B45309]" />
          </div>

          <div className="mt-8">
            <h2 className="font-serif text-3xl font-bold leading-tight text-[#1C1917]">
              Narsi Ayyagari
            </h2>
            <p className="mt-2 font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#B45309]">
              Co-Founder & General Partner
            </p>
          </div>

          <div className="mt-8 grid grid-cols-3 overflow-hidden rounded-lg border border-[#D8C29A] bg-[#FDFAF3] shadow-sm">
            {stats.map(stat => <div key={stat.label} className="border-b border-r border-[#D8C29A] p-4 text-center [&:nth-child(3n)]:border-r-0 [&:nth-last-child(-n+3)]:border-b-0">
                <div className="font-serif text-lg font-semibold text-[#1C1917]">
                  {stat.value}
                </div>
                <div className="mt-1 font-mono text-[9px] uppercase tracking-[0.12em] text-[#1C1917]/55">
                  {stat.label}
                </div>
              </div>)}
          </div>
        </aside>

        <div className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
        transitionDelay: "150ms"
      }}>
          <p className="max-w-4xl text-lg leading-relaxed text-[#1C1917]/70">
            25+ years of institutional fund operations, PE capital deployment,
            and multi-geography investment management. As COO of a $1B global PE
            fund and GP of a $400M+ VC platform, Narsi has built and operated
            the governance infrastructure, LP reporting systems, and capital
            deployment processes that institutional LPs require.
          </p>

          <div className="my-10 h-px w-full bg-[#D8C29A]" />

          <div className="mb-8">
            <span className="font-mono text-xs font-bold uppercase tracking-[0.32em] text-[#B45309]">
              Value-Add to N2N Capital
            </span>
          </div>

          <div className="grid gap-7 md:grid-cols-2">
            {valueAdds.map((item, i) => <div key={item.title} className={`rounded-xl border border-[#D8C29A] bg-white/60 p-7 shadow-sm transition-all duration-700 hover:-translate-y-1 hover:border-[#B45309]/40 hover:shadow-md ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
            transitionDelay: `${250 + i * 100}ms`
          }}>
                <h3 className="font-serif text-xl font-bold leading-snug text-[#1C1917]">
                  {item.title}
                </h3>
                <p className="mt-4 text-sm leading-relaxed text-[#1C1917]/65">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}